import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService, dataPlaneService } from "../dataMapperServices";
import {
  NOT_CONNECTED_REASONS,
  operatorTakesValue,
  refusalReason,
  sameId,
  type DataMapperFilterOperator,
  type DataMapperProfile,
  type DataMapperRowPage,
  type DataMapperSourceColumn,
  type DataMapperSourceDataPage,
  type DataMapperSourceTable,
} from "../dataMapper.types";
import { isSessionEnded } from "stores/sessionGuard";

/** The Data Source route, where a tab with no open source is sent. */
export const DATA_MAPPER_SOURCE_ROUTE = "Data Mapper Source";

/** Rows asked for per page. The API may answer fewer (it pages by bytes) and caps the limit. */
export const BROWSE_PAGE_SIZES = [25, 50, 100, 250, 500] as const;
export const DEFAULT_BROWSE_PAGE_SIZE = 100;

const GONE_REASONS = new Set(["profile-not-found", "profile-not-in-client", "client-not-found"]);

export interface BrowseFilter {
  /** A client-side key for the row in the filter editor. */
  key: number;
  column: string;
  operator: DataMapperFilterOperator;
  value: string;
  enabled: boolean;
}

export interface BrowseSort {
  column: string;
  direction: "asc" | "desc";
}

/** A where condition exactly as `data-mapper-source-data` validates it. */
export interface WhereCondition {
  column: string;
  operator: DataMapperFilterOperator;
  value?: string | string[];
}

/** The applied filters as the API's `where`: complete, enabled rows only; `in` as a list. */
export function toWhere(filters: readonly BrowseFilter[]): WhereCondition[] {
  const out: WhereCondition[] = [];
  for (const f of filters) {
    if (!f.enabled || !f.column) continue;
    if (!operatorTakesValue(f.operator)) {
      out.push({ column: f.column, operator: f.operator });
    } else if (f.operator === "in") {
      const values = f.value
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v !== "");
      if (values.length > 0) out.push({ column: f.column, operator: "in", value: values });
    } else if (f.value !== "") {
      out.push({ column: f.column, operator: f.operator, value: f.value });
    }
  }
  return out;
}

/**
 * Data Mapper → Browse: the open source's tables, one table's columns, and a
 * paged, filtered, sorted grid of its rows (the standalone tool's Explore table
 * picker and Source Data panel).
 *
 * WHAT IT HOLDS. The table list, the open table's columns and the current page
 * of rows are the find RESULTS of the data plane — rows of a client's source
 * database, not HQ entities: they have no HQ id, no socket events and no place
 * in a shared cache (the data plane answers byte-paged envelopes a ServiceStore
 * cannot read). They are held here for the page's life the way
 * InstanceUsersStore holds an instance's users, and dropped on exit. The
 * selection itself is ids on DataMapperViewStore; the open table is its name.
 *
 * PAGING. The API keeps every answer under 1 MB, so a page can come back with
 * fewer rows than asked (`truncated`), and the next page starts at the API's
 * `nextCursor`, never at `skip + pageSize`. The previous pages' starts are kept
 * on a stack, so Previous returns exactly to where each page began.
 */
class DataMapperBrowseStore {
  rootStore: RootStore;

  // ---- Tables -----------------------------------------------------------------
  tables: DataMapperSourceTable[] = [];
  /** The `$skip` of the next page of tables, or null when the list is complete. */
  tablesNextSkip: number | null = null;
  tablesTruncated = false;
  tablesLoading = false;
  tablesLoaded = false;
  tablesError: string | null = null;
  tableSearch = "";
  /** The tab has no source open for this selection: the page says where to connect. */
  notConnected = false;

  // ---- The open table -----------------------------------------------------------
  selectedTable: string | null = null;
  columns: DataMapperSourceColumn[] = [];
  columnsLoading = false;
  columnsError: string | null = null;
  /** Columns shown in the grid (and asked for); null = all. */
  visibleColumns: string[] | null = null;

  filters: BrowseFilter[] = [];
  /** The filters the grid was loaded with (Apply copies `filters` here). */
  appliedFilters: BrowseFilter[] = [];
  sort: BrowseSort | null = null;
  pageSize: number = DEFAULT_BROWSE_PAGE_SIZE;

  // ---- The current page -----------------------------------------------------------
  rows: Record<string, unknown>[] = [];
  /** Where the current page starts. */
  skip = 0;
  /** Where each earlier page started, most recent last (for Previous). */
  pageStarts: number[] = [];
  nextCursor: number | null = null;
  truncated = false;
  total: number | null = null;
  dataLoading = false;
  dataError: string | null = null;

  private tablesSeq = 0;
  private tableSeq = 0;
  private dataSeq = 0;
  private filterKey = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  get filteredTables(): DataMapperSourceTable[] {
    const q = this.tableSearch.trim().toLowerCase();
    if (!q) return this.tables;
    return this.tables.filter((t) => t.fullName.toLowerCase().includes(q));
  }

  /** The grid's columns, in the table's order. */
  get gridColumns(): string[] {
    const all = this.columns.map((c) => c.name);
    if (!this.visibleColumns) return all;
    const chosen = new Set(this.visibleColumns);
    return all.filter((c) => chosen.has(c));
  }

  get hasPrevious(): boolean {
    return this.pageStarts.length > 0;
  }

  get hasNext(): boolean {
    return this.nextCursor !== null;
  }

  /** "Rows 101–180 of 2,000" for the current page. */
  get rangeLabel(): string {
    if (this.rows.length === 0) return this.total === 0 ? "No rows" : "";
    const from = this.skip + 1;
    const to = this.skip + this.rows.length;
    const of = this.total !== null ? ` of ${this.total.toLocaleString()}` : "";
    return `Rows ${from.toLocaleString()}–${to.toLocaleString()}${of}`;
  }

  get filtersDirty(): boolean {
    return JSON.stringify(toWhere(this.filters)) !== JSON.stringify(toWhere(this.appliedFilters));
  }

  get activeFilterCount(): number {
    return toWhere(this.appliedFilters).length;
  }

  // ---- Loading -------------------------------------------------------------------

  /** Route entry: bind the selection and list the source's tables. */
  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    const clientId = this.selection?.selectedClientId;
    if (this.canUse && clientId) {
      // So the header can name the profile after a reload; its failure is the Profiles page's to show.
      void dataMapperService(this.rootStore, "data-mapper-profiles")
        ?.find({ query: { clientId } })
        ?.catch(() => undefined);
    }
    await this.loadTables();
    // The header's refresh re-enters the route without leaving it: the open
    // table survives (exit's reset clears it), so reload the page it was on,
    // keeping the Previous history (Codex r2).
    if (this.selectedTable && !this.notConnected) await this.refreshPage();
  }

  /** Handle a data-plane refusal: not connected, a selection that is gone, or a sentence. */
  private refusal(err: unknown, fallback: string): string | null {
    const reason = refusalReason(err) ?? "";
    if (NOT_CONNECTED_REASONS.has(reason)) {
      this.notConnected = true;
      return null;
    }
    if (GONE_REASONS.has(reason)) {
      const profileId = this.profileId;
      if (profileId) this.selection?.forgetProfile(profileId);
      return "The profile that was open no longer exists. Choose one on the Profiles page.";
    }
    return loadErrorMessage(err, fallback);
  }

  /** List the tables; `more` appends the next byte-page at `tablesNextSkip`. */
  async loadTables(more = false): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-source-tables");
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile || !service) return;
    if (more && this.tablesNextSkip === null) return;
    const seq = ++this.tablesSeq;
    const $skip = more ? (this.tablesNextSkip as number) : 0;
    this.tablesLoading = true;
    this.tablesError = null;
    if (!more) this.notConnected = false;
    try {
      const page = (await service.find({
        query: { ...selection.dataPlaneQuery, $skip },
      })) as DataMapperRowPage<DataMapperSourceTable>;
      runInAction(() => {
        if (seq !== this.tablesSeq) return;
        const rows = Array.isArray(page?.rows) ? page.rows : [];
        this.tables = more ? [...this.tables, ...rows] : rows;
        this.tablesNextSkip = page?.nextCursor ?? null;
        this.tablesTruncated = page?.truncated === true;
        this.tablesLoaded = true;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (seq !== this.tablesSeq) return;
        this.tablesError = this.refusal(err, "The source's tables could not be listed.");
        if (!more) this.tables = [];
        this.tablesLoaded = true;
      });
    } finally {
      runInAction(() => {
        if (seq === this.tablesSeq) this.tablesLoading = false;
      });
    }
  }

  setTableSearch(q: string) {
    this.tableSearch = q;
  }

  /** Open a table: its columns (source-tables get), then its first page of rows. */
  async selectTable(fullName: string): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-source-tables");
    const selection = this.selection;
    if (!service || !selection?.hasProfile) return;
    const seq = ++this.tableSeq;
    this.dataSeq++;
    this.selectedTable = fullName;
    this.columns = [];
    this.visibleColumns = null;
    this.filters = [];
    this.appliedFilters = [];
    this.sort = null;
    this.clearPage();
    this.columnsLoading = true;
    this.columnsError = null;
    this.dataError = null;
    try {
      const details = (await service.get(fullName, {
        query: { ...selection.dataPlaneQuery },
      })) as DataMapperSourceTable;
      runInAction(() => {
        if (seq !== this.tableSeq) return;
        this.columns = Array.isArray(details?.columns) ? details.columns : [];
      });
    } catch (err) {
      runInAction(() => {
        if (seq === this.tableSeq) { if (!isSessionEnded(err)) this.columnsError = this.refusal(err, "The table's columns could not be read."); }
      });
      return;
    } finally {
      runInAction(() => {
        if (seq === this.tableSeq) this.columnsLoading = false;
      });
    }
    if (seq === this.tableSeq) await this.loadPage(0);
  }

  private clearPage() {
    this.rows = [];
    this.skip = 0;
    this.pageStarts = [];
    this.nextCursor = null;
    this.truncated = false;
    this.total = null;
  }

  /** The source-data find for the open table, starting at `skip`. */
  private query(skip: number): Record<string, unknown> {
    const query: Record<string, unknown> = {
      ...(this.selection?.dataPlaneQuery ?? {}),
      table: this.selectedTable,
      $limit: this.pageSize,
      $skip: skip,
    };
    if (this.visibleColumns) query.columns = this.gridColumns;
    const where = toWhere(this.appliedFilters);
    if (where.length > 0) query.where = where;
    if (this.sort) query.orderBy = `${this.sort.column}:${this.sort.direction}`;
    return query;
  }

  /**
   * Load the page starting at `skip`. `history` says how the page stack moves:
   * "reset" (a new query: first page), "push" (Next), "pop" (Previous) or
   * "keep" (a refresh of the same page).
   */
  async loadPage(skip: number, history: "reset" | "push" | "pop" | "keep" = "reset"): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-source-data");
    if (!service || !this.selectedTable || !this.selection?.hasProfile) return;
    const seq = ++this.dataSeq;
    const previousStart = this.skip;
    this.dataLoading = true;
    this.dataError = null;
    try {
      const page = (await service.find({ query: this.query(skip) })) as DataMapperSourceDataPage;
      runInAction(() => {
        if (seq !== this.dataSeq) return;
        if (history === "reset") this.pageStarts = [];
        else if (history === "push") this.pageStarts = [...this.pageStarts, previousStart];
        else if (history === "pop") this.pageStarts = this.pageStarts.slice(0, -1);
        // "keep": a refresh of the same page leaves the history as it was (Codex r1).
        this.rows = Array.isArray(page?.rows) ? page.rows : [];
        this.skip = typeof page?.skip === "number" ? page.skip : skip;
        this.nextCursor = page?.nextCursor ?? null;
        this.truncated = page?.truncated === true;
        this.total = typeof page?.total === "number" ? page.total : null;
      });
    } catch (err) {
      runInAction(() => {
        if (seq === this.dataSeq) { if (!isSessionEnded(err)) this.dataError = this.refusal(err, "The rows could not be loaded."); }
      });
    } finally {
      runInAction(() => {
        if (seq === this.dataSeq) this.dataLoading = false;
      });
    }
  }

  /** The next page starts where the API said this one stopped (it pages by bytes). */
  async nextPage(): Promise<void> {
    if (this.nextCursor === null || this.dataLoading) return;
    await this.loadPage(this.nextCursor, "push");
  }

  async previousPage(): Promise<void> {
    if (this.pageStarts.length === 0 || this.dataLoading) return;
    await this.loadPage(this.pageStarts[this.pageStarts.length - 1], "pop");
  }

  async refreshPage(): Promise<void> {
    if (!this.selectedTable) return;
    // Reload where this page starts, keeping the history behind it.
    await this.loadPage(this.skip, "keep");
  }

  async setPageSize(size: number): Promise<void> {
    this.pageSize = size;
    await this.loadPage(0);
  }

  // ---- Columns, sort, filters ----------------------------------------------------

  async toggleColumn(name: string): Promise<void> {
    const current = new Set(this.visibleColumns ?? this.columns.map((c) => c.name));
    if (current.has(name)) {
      if (current.size === 1) return; // at least one column
      current.delete(name);
    } else {
      current.add(name);
    }
    this.visibleColumns = current.size === this.columns.length ? null : [...current];
    await this.loadPage(0);
  }

  async showAllColumns(): Promise<void> {
    if (this.visibleColumns === null) return;
    this.visibleColumns = null;
    await this.loadPage(0);
  }

  /** Header click: ascending → descending → unsorted. */
  async toggleSort(column: string): Promise<void> {
    if (!this.sort || this.sort.column !== column) this.sort = { column, direction: "asc" };
    else if (this.sort.direction === "asc") this.sort = { column, direction: "desc" };
    else this.sort = null;
    await this.loadPage(0);
  }

  addFilter() {
    this.filters = [
      ...this.filters,
      { key: ++this.filterKey, column: this.columns[0]?.name ?? "", operator: "eq", value: "", enabled: true },
    ];
  }

  updateFilter(key: number, patch: Partial<Omit<BrowseFilter, "key">>) {
    this.filters = this.filters.map((f) => (f.key === key ? { ...f, ...patch } : f));
  }

  removeFilter(key: number) {
    this.filters = this.filters.filter((f) => f.key !== key);
  }

  async applyFilters(): Promise<void> {
    this.appliedFilters = this.filters.map((f) => ({ ...f }));
    await this.loadPage(0);
  }

  async clearFilters(): Promise<void> {
    this.filters = [];
    if (this.appliedFilters.length === 0) return;
    this.appliedFilters = [];
    await this.loadPage(0);
  }

  goToSource() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_SOURCE_ROUTE);
  }

  /** Route exit: drop everything read from the source (it is a client's data). */
  reset() {
    this.tablesSeq++;
    this.tableSeq++;
    this.dataSeq++;
    this.tables = [];
    this.tablesNextSkip = null;
    this.tablesTruncated = false;
    this.tablesLoading = false;
    this.tablesLoaded = false;
    this.tablesError = null;
    this.tableSearch = "";
    this.notConnected = false;
    this.selectedTable = null;
    this.columns = [];
    this.columnsLoading = false;
    this.columnsError = null;
    this.visibleColumns = null;
    this.filters = [];
    this.appliedFilters = [];
    this.sort = null;
    this.clearPage();
    this.dataLoading = false;
    this.dataError = null;
  }
}

export default DataMapperBrowseStore;
